'use client';

import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { formatCount } from '@/lib/discover/formatCount';
import { formatDiscoverScopeCaptionPhrase } from '@/lib/discover/resultsCountCopy';
import type { DateValue } from '@/lib/discover/url-state';

/**
 * Format a URL `date` (or omitted) into the scope caption's `{dateLabel}`.
 * Thin wrapper over {@link formatDiscoverScopeCaptionPhrase} so the caption,
 * rail heads, and results bar share one map (WEB-946). Prefer
 * `facets.scopeKey` at connected call sites via
 * {@link resolveDiscoverScopeKey}; this helper is the URL-only fallback.
 */
export function formatDateScopeLabel(date: DateValue | undefined): string {
  return formatDiscoverScopeCaptionPhrase(date ?? 'upcoming');
}

export interface DiscoverScopeCaptionViewProps {
  /** Number of category slots rendered (always the canonical ten). */
  readonly categoryCount: number;
  /**
   * Total events matching the current query scope. `null` = unresolved —
   * skeleton the count rather than painting `0` (WEB-786).
   */
  readonly eventCount: number | null;
  /** Date-scope phrase from {@link formatDateScopeLabel} (e.g. `"tonight"`). */
  readonly dateLabel: string;
  /** Wrapper class names. */
  readonly className?: string;
}

/**
 * Pure presentational scope caption (WEB-659 / DPL-09) — anchors the
 * category facet counts to a stated scope instead of leaving them floating:
 * `"10 categories · 47 events tonight citywide"`.
 *
 * WEB-1228 (R7): the Free-vibe price disclosure (`priceLabel`, WEB-661) is
 * gone with the Vibes facet that wrote it.
 */
export function DiscoverScopeCaptionView({
  categoryCount,
  eventCount,
  dateLabel,
  className,
}: DiscoverScopeCaptionViewProps): React.ReactElement {
  const categoryNoun = categoryCount === 1 ? 'category' : 'categories';
  const eventNoun = eventCount === 1 ? 'event' : 'events';

  return (
    <p
      data-testid="discover-scope-caption"
      className={cn('px-1 text-micro text-muted-foreground', className)}
    >
      {formatCount(categoryCount)} {categoryNoun}
      {' · '}
      {eventCount == null ? (
        // WEB-1190 D5 — no "events" word and no pulse bar until the number
        // exists. The status text stays for assistive tech.
        <span className="sr-only" role="status" data-testid="discover-scope-event-count-skeleton">
          Loading
        </span>
      ) : (
        <>
          {formatCount(eventCount)} {eventNoun}
        </>
      )}{' '}
      {dateLabel} citywide
    </p>
  );
}
